<script lang="ts">
import { defineComponent, PropType } from 'vue';
import {
  getFirstAvailableVariant,
  getVariantCombinedLabel,
  getVariantOptionsLabel,
  getVariantQuantity,
  isMultipleProduct,
  isVariantOutOfStock,
} from '@monolith/legacy/services/product-variants';
import { QuantityCounter } from '@bc/shared/components';
import Product, { Variant, VariantLayout } from '@monolith/legacy/types/product';
import { useStore } from '@core/composables/use-store';
import { formatDate } from '@monolith/legacy/services/date-locale';
import type { QuantityCounterSource } from '@monolith/legacy/services/analytics/events/quantity-counter-interacted-event';

export default defineComponent({
  name: 'VariantForm',
  components: { QuantityCounter },
  props: {
    product: {
      type: Object as PropType<Product>,
      required: true,
    },
    layout: {
      type: String as PropType<VariantLayout>,
      default: VariantLayout.Vertical,
      validator: (value: VariantLayout) => Object.values(VariantLayout).includes(value),
    },
    constrainWidth: {
      type: Boolean,
      default: false,
    },
    onDrawer: {
      type: Boolean,
      default: false,
    },
    initialVariantId: {
      type: Number,
      default: null,
    },
  },
  emits: ['selected-variant'],
  setup() {
    const store = useStore();

    const itemByVariantId = (id: number) => store.getters['cart/itemByVariantId'](id);

    return {
      itemByVariantId,
    };
  },
  data() {
    const { id } = getFirstAvailableVariant(this.product);
    const hasInitialVariant =
      this.initialVariantId != null && this.product.variants.some((variant) => variant.id === this.initialVariantId);
    return {
      quantity: this.product.unit_multiplier,
      selectedVariantId: hasInitialVariant ? this.initialVariantId : id,
      translatedLabels: {
        size: this.$t('global.productVariant.size.lbl'),
        color: this.$t('global.productVariant.color.lbl'),
        other: this.$t('global.productVariant.other.lbl'),
      },
    };
  },
  computed: {
    isHorizontal(): boolean {
      return this.layout === VariantLayout.Horizontal;
    },
    isVertical(): boolean {
      return this.layout === VariantLayout.Vertical;
    },
    variants(): Variant[] {
      return this.product.variants.filter((variant) => !isVariantOutOfStock(variant));
    },
    isMultipleProduct(): boolean {
      return isMultipleProduct(this.variants);
    },
    computedLabel(): string {
      return getVariantOptionsLabel(this.variants, (name) => this.translatedLabels[name]);
    },
    computedVariants(): Array<{ value: number; label: string; subLabel: string | null }> {
      return this.variants.map((variant) => ({
        value: variant.id,
        subLabel: variant.available_at
          ? this.$t('product.dropdown.variant.availability', { date: formatDate(variant.available_at, 'P') })
          : null,
        label: getVariantCombinedLabel(variant),
      }));
    },
    selectedVariant(): Variant | undefined {
      return this.variants.find(({ id }) => id === this.selectedVariantId);
    },
    minQuantity(): number {
      const alreadyInCart = Boolean(this.selectedVariant && this.itemByVariantId(this.selectedVariant.id));
      return alreadyInCart ? 0 : this.product.unit_multiplier;
    },
    maxQuantityForCounter(): number | undefined {
      if (!this.selectedVariant) {
        return this.product.unit_multiplier;
      }
      const qty = getVariantQuantity(this.selectedVariant);
      if (!qty || qty < 0) {
        return undefined;
      }
      return qty;
    },
    trackingSource(): QuantityCounterSource {
      return this.onDrawer ? 'product_drawer' : 'product_page';
    },
  },
  watch: {
    quantity(): void {
      this.emitSelectedVariant();
    },
    selectedVariant: {
      immediate: true,
      handler(): void {
        this.emitSelectedVariant();
      },
    },
  },
  methods: {
    emitSelectedVariant(): void {
      this.$emit('selected-variant', {
        variant: this.selectedVariant,
        quantity: this.quantity / this.product.unit_multiplier,
      });
    },
  },
});
</script>

<template>
  <div
    class="variant-form"
    :class="{
      'variant-form--horizontal': isHorizontal,
      'variant-form--vertical': isVertical,
    }"
  >
    <AkSelect
      v-if="isMultipleProduct"
      v-model="selectedVariantId"
      :label="computedLabel"
      :options="computedVariants"
      class="variant-form__select"
    />
    <div
      id="variant-options__quantity"
      class="variant-form__quantity-row"
      :class="{ 'variant-form__quantity-row--constrained': constrainWidth }"
    >
      <div class="variant-form__quantity-field">
        <QuantityCounter
          v-model="quantity"
          :step="product.unit_multiplier"
          :min="minQuantity"
          :max="maxQuantityForCounter"
          :disabled="!selectedVariant"
          :tracking-context="{ source: trackingSource, productId: product.id }"
          data-testid="productQuantitySelect"
        />
      </div>
      <slot />
    </div>
  </div>
</template>

<style scoped lang="scss">
@import '@css/vue-import';

.variant-form {
  @apply ds-flex;

  &--horizontal {
    @apply ds-gap-4 ds-flex-col xl:ds-gap-2 xl:ds-flex-row;
  }

  &--vertical {
    @apply ds-gap-4 ds-flex-col;
  }

  &__select {
    @apply ds-flex-grow;
  }

  &__quantity-row {
    @apply ds-flex ds-flex-col md:ds-flex-row md:ds-items-end ds-gap-2;

    &--constrained {
      @apply md:ds-max-w-[50%];
    }
  }

  &__quantity-field {
    @apply ds-flex-1;
  }
}
</style>
